import { BeHypeWithdrawalAbi } from '../abis/behype'
import type { LstWithdrawalRequest } from '../types'
import { toBigInt, type LstWithdrawalReader } from './shared'

/**
 * Hyperbeat beHYPE — per-user withdrawal-request enumeration on the
 * WithdrawManager.
 *
 * Stage 1: `getUserUnclaimedWithdrawals(user)` → open request ids.
 * Stage 2: per id, `withdrawalQueue(id)` (amounts) +
 *          `canClaimWithdrawal(id)` (finalization flag).
 *
 * The WithdrawManager exposes no per-request ready timestamp, so
 * `status` is derived from `canClaimWithdrawal` and `readyAt` is left
 * unset (mirrors the Kelp reader).
 */
export const readerBeHype: LstWithdrawalReader = {
  fetch: async (user, multicallRetry, chainId, entry) => {
    const manager = entry.withdrawalContract
    if (!manager) return []

    let stage1: unknown[]
    try {
      stage1 = (await multicallRetry({
        chain: chainId,
        calls: [
          {
            address: manager,
            name: 'getUserUnclaimedWithdrawals',
            params: [user],
          },
        ] as unknown as never[],
        abi: [BeHypeWithdrawalAbi] as unknown as never,
      })) as unknown[]
    } catch {
      return []
    }
    const ids = stage1[0] as unknown[] | undefined
    if (!Array.isArray(ids) || ids.length === 0) return []

    const calls: Array<{ address: string; name: string; params: unknown[] }> =
      []
    for (const id of ids) {
      calls.push({ address: manager, name: 'withdrawalQueue', params: [id] })
      calls.push({ address: manager, name: 'canClaimWithdrawal', params: [id] })
    }
    let stage2: unknown[]
    try {
      stage2 = (await multicallRetry({
        chain: chainId,
        calls: calls as unknown as never[],
        abi: calls.map(() => BeHypeWithdrawalAbi) as unknown as never,
      })) as unknown[]
    } catch {
      return []
    }

    const out: LstWithdrawalRequest[] = []
    for (let i = 0; i < ids.length; i++) {
      const q = stage2[i * 2] as
        | { hypeAmount?: unknown; claimed?: unknown }
        | unknown[]
        | undefined
      const canClaim = Boolean(stage2[i * 2 + 1])
      if (!q) continue
      const hypeAmount = Array.isArray(q)
        ? toBigInt(q[2])
        : toBigInt(q.hypeAmount)
      if (hypeAmount === undefined) continue
      out.push({
        lst: entry.lst,
        brand: entry.brand,
        symbol: entry.symbol,
        requestId: String(toBigInt(ids[i]) ?? i),
        amountUnderlying: hypeAmount.toString(),
        status: canClaim ? 'claimable' : 'pending',
      })
    }
    return out
  },
}
